/**
 * The closed vocabulary.
 *
 * Everything a layout spec may *name* comes from these sets. This is the
 * "closed and small" half of decision 0010 — the half that must stay closed
 * for specs to be validatable and safely poolable.
 *
 * The rule that governs every list here: a spec may NAME a thing from a closed
 * set; it may never COMPUTE one. Adding a member is a deliberate act with a
 * version bump, never something generation does on the fly.
 */

/** Semantic roles a slot binds to. Never field names — that is what makes a
 *  spec portable across domains and safe to pool. */
export const ROLES = [
  'title', 'subtitle', 'body', 'caption',
  'author', 'date', 'duration', 'origin',
  // A SPAN needs two ends, and one `date` role cannot carry them.
  //
  // Two manifest keys may both bind role `date`, and an object populating both
  // returns that role as an ARRAY — which is why the calendar counts such an
  // object as undated rather than guessing which of two days it happens on.
  // Indistinguishable values are not a span; "these two dates, in this order"
  // is a different claim and it needs different words.
  //
  // Compound names, like `media.hero` and `relation.peer` before them: the
  // dotted prefix is what says these belong to `date` rather than being two
  // unrelated ideas that happen to be dates.
  'date.start', 'date.end',
  'score', 'status', 'quantity', 'identifier',
  'media.hero', 'media.thumb', 'media.poster-frame',
  // Decision 0014, extension 1: media is not ONE kind. A trailer, a commentary
  // track and a poster have genuinely different presentation contracts — a
  // video has a duration and a poster frame, audio has a waveform and no aspect
  // ratio at all, a still has an aspect and nothing else. One `media` role and
  // one still renderer could not express Joaquín's own example, which is a
  // Movie carrying a trailer, images, a poster, a transcript and audio.
  //
  // The URL still never enters the vocabulary. A trailer's ROLE is `media.video`;
  // where it lives is data, resolved through the private binding manifest at
  // render time and dropped at the pool boundary like every other value.
  'media.video', 'media.audio', 'media.still',
  // A PAGED document — a PDF, a scan, a contract. `ACCEPTANCE.md`'s Fabric row
  // asks for "everything renderable — video, audio, PDF, transcript", and this
  // is the one of the four with nowhere to bind.
  //
  // Its own role rather than a still, because the presentation contract is
  // genuinely a third thing. A still has an aspect and nothing else; a
  // recording has a timeline and a transport; a document has PAGES, its own
  // scroll, and a viewer that is not ours. Filed under `media.` because the
  // value is a blob like the others and reaches the renderer the same way.
  'media.document',
  'transcript',
  'relation.peer', 'relation.parent', 'relation.child',
  // Retrieval roles — Mem/Fabric render heterogeneous results ranked by
  // relevance with citations, and none of that had anywhere to bind.
  'rank', 'confidence', 'source',
] as const;
export type Role = (typeof ROLES)[number];

/** What kind of value a role carries. Decides which predicates are legal
 *  against it — Notion's file property accepting only emptiness tests is not
 *  an arbitrary restriction, it falls out of what the type can answer. */
/** `av` sits alongside `media` because time-based media is a different
 *  presentation contract, not a different source. A renderer branches on this
 *  rather than on the role name, which is what keeps the branch closed.
 *
 *  `doc` sits alongside both for the same reason and a sharper one. A paged
 *  document is not a picture and not a recording, and the cost of pretending
 *  otherwise is not an abstraction complaint — it is a wrong LOOK, silently.
 *  Filed as `media`, a PDF would have been eligible as a gallery hero and been
 *  handed a 2/3 box and a `background-image`; filed as `av`, it would have been
 *  listed among an object's recordings. Both render something plausible and
 *  wrong, which is the failure mode `av-review.test.ts` records nine times. */
export const ROLE_KINDS = [
  'text', 'number', 'date', 'enum', 'media', 'av', 'ref', 'score', 'doc',
] as const;
export type RoleKind = (typeof ROLE_KINDS)[number];

/**
 * Kinds no grouping can use, at either level.
 *
 * A group heading is a NAME A PERSON READS. These four have no such rendering:
 * a reference expands to an object and formats as `[object Object]`, and media,
 * av and document roles are blobs whose identity is not text. Grouping by one
 * collapses every distinct value under a single meaningless heading — worse
 * than refusing, because it looks like it worked.
 *
 * ONE set, exported, because the same list was written twice and drifted the
 * first time it was touched: @Codex found `subGroupBy` accepting all four while
 * `groupBy` refused three of them, then found the repair still accepting `doc`
 * while its own commit message claimed otherwise. A list maintained in two
 * places is a list with one place for the next omission to hide.
 */
export const UNGROUPABLE_KINDS: readonly RoleKind[] = ['media', 'av', 'ref', 'doc'];

/**
 * Kinds that a person SEES as a picture, a recording or a document — the ones
 * whose presence makes a pool media-first rather than text-first.
 *
 * ONE set, exported, for the reason `UNGROUPABLE_KINDS` above states about
 * itself. This particular list was written out by value in THREE places —
 * `baseline.ts`, and both `deriveShape` implementations — and two of them
 * have now been short of a kind the third already had.
 *
 * `av` was added after @Codex found a pool of nothing but video deriving
 * `hasMedia: false` and being typeset as prose — *"the generator sized and
 * typeset it as if the recordings were not there."* `baseline.ts` answered by
 * naming the predicate once, in a docstring that says *"named once so the next
 * kind added cannot repeat it"*, and it did include `doc`. But that name was
 * private to one file, so the two `deriveShape` implementations kept their own
 * copies of the same test written out by value, and both omitted `doc`.
 *
 * @Codex, P2 on `addf8bf`: *"When a pool contains PDFs but no still image or
 * recording, its `media.document` role has kind `doc`, so this predicate
 * reports `hasMedia: false`. The generator consequently applies the text-heavy
 * appetite and emits `target.mediaWeight: 'text'` even though the resulting
 * cards render documents."*
 *
 * `ref` is the difference from `UNGROUPABLE_KINDS` and it is deliberate. Both
 * sets exclude it from an OUTER heading, for related but distinct reasons — a
 * blob's identity is not text, the store's fold keys a reference by its
 * identity — and only this one asks what a person SEES, where a reference
 * draws as a LINK rather than as a picture. (A reference IS admitted as the
 * second grouping, a lane named by its target's title — `validate.ts` says
 * why the two positions differ.) The two sets overlap; neither is the other's
 * shorthand.
 */
export const VISUAL_KINDS: readonly RoleKind[] = ['media', 'av', 'doc'];

/** Whether a kind draws as a picture, a recording or a document. Read from
 *  `VISUAL_KINDS` rather than restated, so a fourth such kind reaches every
 *  caller at once — which is the whole of what went wrong above. */
export const isVisualKind = (kind: RoleKind): boolean => VISUAL_KINDS.includes(kind);

/**
 * How a shape names the JOINT distribution of two enum roles.
 *
 * A shape carries `enumCardinality[role]` — how many distinct values a role
 * holds — and that is a MARGINAL count. It cannot answer the only question a
 * second grouping turns on: does the inner role actually divide the outer
 * one's columns, or are the two correlated so tightly that every column holds
 * a single inner value? Both roles pass every marginal test in that case and
 * the board draws no sub-headings at all.
 *
 * Order-free on purpose: the number of distinct `(a, b)` pairs is the same
 * whichever role is asked about first, so one entry serves both directions and
 * there is no second entry to fall out of step with it. @Codex found the
 * generator emitting `subGroupBy` on marginal evidence alone.
 */
export const enumPairKey = (a: Role, b: Role): string =>
  a < b ? `${a}|${b}` : `${b}|${a}`;

/**
 * Which source a derivative role is the cheap picture OF.
 *
 * Named here rather than in either consumer because both need it and a second
 * copy is two descriptions of one rule, free to drift — the failure
 * `UNGROUPABLE_KINDS` already records happening to the kind guard.
 *
 * `apps/demo/src/derivatives.ts` decides which derivative to MAKE from a source
 * media type; this says what an existing one stands in for, which is the
 * question a generator asks and a mime cannot answer. Task `0131`.
 */
export const DERIVATIVE_OF: Readonly<Partial<Record<Role, Role>>> = {
  'media.thumb': 'media.hero',
  'media.poster-frame': 'media.video',
};

export const ROLE_KIND: Record<Role, RoleKind> = {
  'title': 'text', 'subtitle': 'text', 'body': 'text', 'caption': 'text',
  'author': 'text', 'date': 'date', 'duration': 'number', 'origin': 'enum',
  'date.start': 'date', 'date.end': 'date',
  'score': 'score', 'status': 'enum', 'quantity': 'number', 'identifier': 'text',
  'media.hero': 'media', 'media.thumb': 'media', 'media.poster-frame': 'media',
  'media.video': 'av', 'media.audio': 'av', 'media.still': 'media',
  'media.document': 'doc',
  // A transcript is TEXT that happens to belong to a recording. Giving it its
  // own kind would have meant inventing a presentation contract for something
  // that is simply long prose, and the vocabulary already has one of those.
  'transcript': 'text',
  'relation.peer': 'ref', 'relation.parent': 'ref', 'relation.child': 'ref',
  'rank': 'number', 'confidence': 'score', 'source': 'ref',
};

/**
 * Which text roles hold PROSE — text a person writes in paragraphs.
 *
 * A separate statement from `ROLE_KIND` rather than a ninth kind, because it
 * answers a different question. `text` is what the VALUE is, and every role
 * here is still text: a title, a caption and a body all hold a string, and a
 * renderer that branched on a `prose` kind would have to re-learn that they
 * are strings. This says what a person DOES with it, which is what decides
 * whether Enter ends the edit or starts a paragraph.
 *
 * Why that needed saying at all: the edit host bound Enter to commit for every
 * field, so pressing it in a note's body ended the edit instead of breaking the
 * line — no paragraph was ever created, and two paragraphs typed in sequence
 * were stored as one run-on string with the words at the boundary jammed
 * together. Correct for a title, where Enter meaning "done" is exactly right,
 * and it made writing a note of any length impossible. Task 0044 called that
 * "text that renders correctly" and put it second on the list.
 *
 * Deliberately short. `caption`, `subtitle` and `identifier` are single-line by
 * nature and Enter should still finish them; `header` and `footer` are the
 * document-flow slots that are not roles. Adding one here is a claim that a
 * person writes paragraphs into it, and should be as hard to make as any other
 * vocabulary claim.
 */
export const PROSE_ROLES = ['body', 'transcript'] as const;

/** Does a person write paragraphs into this role? */
export const isProse = (role: Role): boolean =>
  (PROSE_ROLES as readonly string[]).includes(role);

/** Predicate operators, gated by role kind. */
export const OPERATORS = [
  'equals', 'not-equals', 'in', 'contains',
  'before', 'after', 'between',
  'greater-than', 'less-than',
  'is-empty', 'is-not-empty',
] as const;
export type Operator = (typeof OPERATORS)[number];

const ALWAYS = ['is-empty', 'is-not-empty'] as const;
export const OPERATORS_BY_KIND: Record<RoleKind, readonly Operator[]> = {
  text:   [...ALWAYS, 'equals', 'not-equals', 'in', 'contains'],
  number: [...ALWAYS, 'equals', 'not-equals', 'greater-than', 'less-than', 'between'],
  score:  [...ALWAYS, 'equals', 'not-equals', 'greater-than', 'less-than', 'between'],
  date:   [...ALWAYS, 'equals', 'before', 'after', 'between'],
  enum:   [...ALWAYS, 'equals', 'not-equals', 'in'],
  ref:    [...ALWAYS, 'equals', 'not-equals', 'in'],
  // Media can only be asked whether it exists. Everything else needs to open
  // the blob, which is the object store's business and not a layout question.
  media:  [...ALWAYS],
  // Same answer for the same reason: asking anything else about a recording
  // means opening it, which is the object store's business and not a layout
  // question.
  av:     [...ALWAYS],
  // And again for a document. A page count would be a tempting third operator
  // and it is the same mistake: knowing how many pages a PDF has means parsing
  // it, and a layout question must never be answered by opening a blob.
  doc:    [...ALWAYS],
};

/** The closed aggregation set. The line decision 0010 turns on: a spec may name
 *  an aggregation; it may never contain an expression. Coda's formulas are the
 *  object store's business — a formula carries a schema and a threshold, which
 *  is both a privacy leak and dead weight in a shared pool. */
export const AGGREGATIONS = ['count', 'sum', 'avg', 'min', 'max', 'first', 'last', 'span'] as const;
export type Aggregation = (typeof AGGREGATIONS)[number];

/**
 * The closed DERIVATION set — Coda's formula columns, without a formula.
 *
 * `ACCEPTANCE.md`'s Coda row asks for "tables with formula columns". Decision
 * 0004 already settled what that can mean here, about aggregations, and the
 * sentence generalizes without amendment:
 *
 *   **The spec may NAME a computation. It may never CONTAIN one.**
 *
 * So a column whose value is computed names one of these words. There is no
 * expression, no operator precedence, no cell references, and deliberately no
 * way to combine two of them — a language is what this refuses to become, for
 * the same reason 0004 gave: an expression carries a schema and a threshold
 * into a shared pool, which is both a privacy leak and dead weight.
 *
 * Every one of these is a function of a SINGLE bound value. That is the
 * property that keeps them honest: nothing here can read another column, so no
 * derivation can depend on layout, on ordering, or on what else the lens
 * happens to be showing.
 *
 * Notably absent: anything involving the current time. `age-in-days` is the
 * obvious next word and it is not here, because a value that changes at
 * midnight makes a rendering that cannot be reproduced from its inputs — and
 * every gate in this repo compares a render to a recorded one. It wants a
 * clock the host supplies, which is a real design, not an extra word.
 */
export const DERIVATIONS = ['year', 'month', 'words', 'characters', 'count'] as const;
export type Derivation = (typeof DERIVATIONS)[number];

/**
 * Which role kinds each derivation can be asked of.
 *
 * A word count of a date is not a smaller answer, it is a meaningless one, and
 * accepting it would put a number in a column whose header says something else.
 * Refused at the schema boundary, the same way a capability channel refuses a
 * role kind it cannot place.
 */
export const DERIVATION_ROLE_KINDS: Record<Derivation, readonly RoleKind[]> = {
  year:       ['date'],
  month:      ['date'],
  words:      ['text'],
  characters: ['text'],
  // A `count` is of things, and the only role kind that holds several is a
  // reference. Counting the characters of a title is `characters`.
  count:      ['ref'],
};

/** What a derived column reads as. The label belongs beside the word rather
 *  than in the renderer: a header is part of what the derivation MEANS, and a
 *  second copy in a formatter is how a column ends up labelled one thing and
 *  computing another. */
export const DERIVATION_LABELS: Record<Derivation, string> = {
  year: 'Year',
  month: 'Month',
  // "Word count", not "Words". A pool is free to label a STORED role anything,
  // and the demo's notes pool labels its `quantity` role exactly "Words" — so
  // the short version put two different columns under one heading, with only
  // their order telling them apart. Found by a gate assertion that looked the
  // column up by its header text and got the wrong one.
  words: 'Word count',
  characters: 'Characters',
  count: 'Count',
};

/** The role kind a derivation PRODUCES, which is what a table column needs to
 *  know to align it. Every one of these is a count or a year, so every one is a
 *  number — but stating it here means a later derivation that yields text
 *  cannot silently be right-aligned with the integers. */
export const DERIVATION_YIELDS: Record<Derivation, RoleKind> = {
  year: 'number', month: 'text', words: 'number', characters: 'number', count: 'number',
};

export function derivationAcceptsRole(derivation: Derivation, role: Role): boolean {
  return DERIVATION_ROLE_KINDS[derivation].includes(ROLE_KIND[role]);
}

/** What a lens is FOR. These were spelled out as inline literal unions in the
 *  schema, which left the pool gate nothing to check a value against — and the
 *  gate runs without validation, so "the schema constrains it" is not an answer
 *  there. Closed sets, named in one place, like every other axis. */
export const CARDINALITIES = ['single', 'small-set', 'large-set'] as const;
export type Cardinality = (typeof CARDINALITIES)[number];

export const MEDIA_WEIGHTS = ['text', 'mixed', 'media'] as const;
export type MediaWeight = (typeof MEDIA_WEIGHTS)[number];

export const USE_CONTEXTS = ['reading', 'authoring', 'browsing', 'comparing', 'studying'] as const;
export type UseContext = (typeof USE_CONTEXTS)[number];

/** Density is not a scalar the generator can free-set. Finding 03 from the
 *  lens prototype and Codex's density-boundary study, from opposite sides:
 *  at the dense end metadata is SUPPRESSED, not shrunk. These are discrete
 *  legible states; a float would produce valid, unreadable layouts. */
export const DENSITIES = ['airy', 'balanced', 'dense'] as const;
export type Density = (typeof DENSITIES)[number];

/** Densities as positions on the same 0..1 appetite axis a generator reasons
 *  on, so a continuous signal selects a discrete state without a lookup. */
export const DENSITY_LEVEL: Record<Density, number> = {
  airy: 0.15, balanced: 0.5, dense: 0.9,
};


/** Typographic *systems*, not numbers. A generator emitting free-valued scales
 *  and leading produces structurally valid layouts that read as templates —
 *  which is the exact failure this product exists to avoid. */
export const TYPE_SYSTEMS = ['editorial', 'utility', 'compact', 'display'] as const;
export type TypeSystem = (typeof TYPE_SYSTEMS)[number];

/**
 * What each system *is*, in measurable terms — so a generator can score a system
 * against an object shape rather than look one up in a table of somebody's
 * opinions. Values are 0..1.
 *
 * This is the vocabulary describing itself. It is not taste: `editorial` really
 * does carry long text better than `compact` does, in the same way a 68-character
 * measure really is easier to read than a 110-character one. What remains taste
 * — *which* of these suits a person — is the taste model's business, and this
 * table has nothing to say about it.
 */
export interface TypeSystemCharacter {
  /** Tolerance for long-form running text. */
  readonly proseFit: number;
  /** How much information it can carry per unit area before failing. */
  readonly densityFit: number;
  /** How well it sits beside dominant imagery rather than competing with it. */
  readonly mediaFit: number;
  /** Suitability as an editing surface, where predictability beats character. */
  readonly editingFit: number;
}

export const TYPE_SYSTEM_CHARACTER: Record<TypeSystem, TypeSystemCharacter> = {
  editorial: { proseFit: 1.0, densityFit: 0.3, mediaFit: 0.5, editingFit: 0.4 },
  utility:   { proseFit: 0.5, densityFit: 0.6, mediaFit: 0.5, editingFit: 1.0 },
  compact:   { proseFit: 0.3, densityFit: 1.0, mediaFit: 0.4, editingFit: 0.6 },
  display:   { proseFit: 0.4, densityFit: 0.2, mediaFit: 1.0, editingFit: 0.3 },
};


export const PALETTES = ['neutral', 'warm', 'cool', 'high-contrast'] as const;
export type Palette = (typeof PALETTES)[number];

export const FLOWS = ['grid', 'stack', 'row', 'flow'] as const;
export type Flow = (typeof FLOWS)[number];

/**
 * Placement — where a node sits relative to the surface, rather than where it
 * sits inside a document. `FLOWS` arranges children *within* a container; it
 * has no word for a sidebar, and decision 0014 is that the absence is why "put
 * the sidebar at the bottom" is currently inexpressible.
 *
 * An algebra, not an enumeration. @Codex's amendment to 0014, and it is the
 * better design: seven mutually exclusive words like `docked-left` encode
 * combinations into an enum, and `left` quietly breaks right-to-left writing
 * modes. Modes compose with a logical edge instead.
 *
 * The line that makes the whole thing tractable, from 0014:
 *
 *   **Placement is spec. Position is view state.**
 *
 * Which edge a panel is docked to is a layout decision, so it is poolable —
 * "people who work this way dock the panel to the inline end" is exactly what
 * the pool should learn. Where the user dragged the bubble to at 14:32 is
 * private, per-device, and meaningless to anyone else. That is why nothing in
 * this file can express a coordinate: the boundary is structural, not filtered.
 */
export const PLACEMENT_MODES = ['inline', 'docked', 'floating', 'anchored'] as const;
export type PlacementMode = (typeof PLACEMENT_MODES)[number];

/** LOGICAL edges, never `left`/`right`. A `docked-left` sidebar is on the wrong
 *  side of an Arabic or Hebrew document, and the bug is invisible to everyone
 *  who does not read one. */
export const DOCK_EDGES = ['block-start', 'block-end', 'inline-start', 'inline-end'] as const;
export type DockEdge = (typeof DOCK_EDGES)[number];

/** Where a floating or anchored panel attaches. Same logical vocabulary as the
 *  dock edges plus a centre, so one mental model covers both. */
export const ATTACHMENTS = [
  'block-start', 'block-end', 'inline-start', 'inline-end', 'center',
] as const;
export type Attachment = (typeof ATTACHMENTS)[number];

/** How much room a placement wants, as INTENT rather than measurement. A
 *  closed set because the alternative is a number, and a number in a poolable
 *  field is a covert channel — the same argument that made `ASPECTS` closed.
 *  The user's actual dragged width is view state and never appears here. */
export const EXTENTS = ['hug', 'compact', 'roomy'] as const;
export type Extent = (typeof EXTENTS)[number];

/** Interaction capabilities, never arbitrary event handlers. @Codex's phrasing
 *  in 0004; it survives contact with the canon. */
export const AFFORDANCES = [
  'select', 'expand', 'reorder', 'edit-inline', 'navigate', 'group-move', 'invoke',
  /** Decision 0016's two relation gestures. Separate words because a grant's
   *  `via` is what stops one gesture performing another write, and attach and
   *  detach are distinct authorities — an attach affordance must not be able to
   *  remove a relation. */
  'attach', 'detach',
  /** A collection that accepts a NEW member from its own face — Notion's
   *  "+ New page" at the foot of a board column, which creates a page in
   *  that column's status. The host's create door, with the group's value
   *  preset; drawn only where a spec declares it and the person may write. */
  'create',
] as const;
export type Affordance = (typeof AFFORDANCES)[number];

/** Mutations a read-write binding may declare (decision 0007). The runtime
 *  refuses any write a binding did not declare, which is what makes the
 *  presentation layer a permission boundary rather than a renderer. */
export const MUTATIONS = [
  'set-value', 'set-enum', 'set-date', 'reorder-within', 'move-between-groups', 'toggle',
  /** Decision 0016. The only two core mutations that create or remove a
   *  normalized relation — set membership, not scalar replacement. */
  'link', 'unlink',
] as const;
export type Mutation = (typeof MUTATIONS)[number];

/** The two relation mutations, named once so nothing has to spell the pair. */
export const RELATION_MUTATIONS = ['link', 'unlink'] as const;
export type RelationMutation = (typeof RELATION_MUTATIONS)[number];

export const isRelationMutation = (m: Mutation): m is RelationMutation =>
  (RELATION_MUTATIONS as readonly string[]).includes(m);

/**
 * Which role kinds each mutation is legal against — the write-side twin of
 * `OPERATORS_BY_KIND`, and now beside it.
 *
 * It lived in `@morph/store` and said so of itself: *"a legality table split
 * across two packages is how the two halves drift. It sits here because the
 * vocabulary is jointly owned under decision 0002 and adding a table to it is
 * a negotiated change, not a unilateral one."* Decision 0016 is that
 * negotiation — accepted, proposed by @Codex — and asks for exactly this:
 * *"Mutation legality will have one exhaustive source of truth in
 * `@morph/spec`, beside `MUTATIONS`."*
 *
 * The drift it predicted had happened. `validateSpec` special-cased ONE pair
 * (`move-between-groups` needs an enum) and accepted everything else, so a
 * spec could declare a write the store would refuse and nothing said so until
 * a person tried it. Measured across `MUTATIONS × ROLES` — 168 pairs, 144 of them illegal:
 * the validator caught 26 and accepted the other **118**, every one a write
 * authorization refuses. A spec that validates and
 * cannot mean anything is the thing 0007 exists to prevent.
 *
 * Every entry is derived, not chosen:
 *  - `move-between-groups` writes a grouping value, so it needs an enum role.
 *  - `toggle` writes a two-state value; the vocabulary has no boolean kind, so
 *    the two-state kind is `enum`.
 *  - `reorder-within` writes an ordering position, which is a number.
 *  - `set-value` is the generic scalar write and covers the remaining scalars.
 *    It deliberately does not cover `ref` or `media`: re-pointing a relation is
 *    a relation mutation's business (0016), and replacing a blob is
 *    ingestion's.
 *
 * In the vocabulary FINGERPRINT, for the reason `DERIVATION_ROLE_KINDS` is:
 * widening it widens what a spec may legally say without one schema byte
 * changing, so a validator that had not heard of the widening would disagree
 * with one that had about what a given version permits.
 */
export const MUTATION_ROLE_KINDS: Record<Mutation, readonly RoleKind[]> = {
  'set-value':           ['text', 'number', 'score'],
  'set-enum':            ['enum'],
  'set-date':            ['date'],
  'toggle':              ['enum'],
  'reorder-within':      ['number'],
  'move-between-groups': ['enum'],
  'link':                ['ref'],
  'unlink':              ['ref'],
};

/**
 * Which `ref` roles are EDGES.
 *
 * Kind is not enough here, and decision 0016 says why: *"the provenance role
 * `source` remains a non-edge `ref` and is never relation-mutable."* Where a
 * value came from is not a relationship between two objects, and a person
 * unlinking their own citation would be editing history rather than a graph.
 *
 * So `link` is legal by KIND against `source` and illegal by ROLE, which is
 * the pair that makes this table necessary rather than derivable — and the
 * reason 0016 asks that agreement tests be role-granular rather than reduced
 * to kinds.
 *
 * `force-graph`'s `edge` channel reads this list rather than repeating it.
 * Those two spellings were the same three roles written twice, which is the
 * shape of every drift this repo has found.
 */
export const RELATION_ROLES = ['relation.peer', 'relation.parent', 'relation.child'] as const;
export type RelationRole = (typeof RELATION_ROLES)[number];

export const isRelationRole = (role: Role): role is RelationRole =>
  (RELATION_ROLES as readonly string[]).includes(role);

/**
 * Where a mutation may be DECLARED — the second axis of legality.
 *
 * A role table alone cannot say what 0016 requires: *"an ordinary
 * `Binding.mutation` on a field, card, capability channel, or other binding
 * site must reject `link` and `unlink`, even when its role is
 * relation-mutable."* `relation.peer + link` is legal in a node slot and
 * illegal in a field, and no property of the role or the mutation distinguishes
 * those two — only the SITE does.
 *
 * The reason is not taste. A relation mutation needs a gesture (attach, or
 * detach of one occurrence) and a payload (an opaque relation-target handle).
 * A field binding has neither, so a spec declaring one there is asking for a
 * write the runtime has no way to perform — the thing 0007 exists to refuse.
 */
export const BINDING_SITES = ['card', 'field', 'media', 'capability-channel', 'node-slot'] as const;
export type BindingSite = (typeof BINDING_SITES)[number];

/** Every site that carries an ordinary `Binding`. A node slot does not: it
 *  declares `mutations` instead, which is the whole distinction above. */
const ORDINARY_SITES = ['card', 'field', 'media', 'capability-channel'] as const;

export const MUTATION_BINDING_SITES: Record<Mutation, readonly BindingSite[]> = {
  'set-value':           ORDINARY_SITES,
  'set-enum':            ORDINARY_SITES,
  'set-date':            ORDINARY_SITES,
  'toggle':              ORDINARY_SITES,
  'reorder-within':      ORDINARY_SITES,
  'move-between-groups': ORDINARY_SITES,
  'link':                ['node-slot'],
  'unlink':              ['node-slot'],
};

/**
 * Whether a mutation may be declared against a role at all. One question, one
 * answer, consumed by `validateSpec` and by store authorization alike.
 *
 * Role-granular, not kind-granular: `link` against `source` passes the kind
 * table and fails here.
 */
export function mutationAcceptsRole(mutation: Mutation, role: Role): boolean {
  if (!MUTATION_ROLE_KINDS[mutation].includes(ROLE_KIND[role])) return false;
  if (isRelationMutation(mutation)) return isRelationRole(role);
  return true;
}

/** Whether a mutation may be declared at a binding site. */
export function mutationAcceptsSite(mutation: Mutation, site: BindingSite): boolean {
  return MUTATION_BINDING_SITES[mutation].includes(site);
}

/** The module registry. Open in principle — modules are REGISTERED, not
 *  enumerated forever — but every entry declares fragmentability, because a
 *  fragmenting host must know whether a child can be split across a page
 *  boundary. A table can; a force-directed graph cannot. */
export const CAPABILITIES = {
  // THE WORKSPACE CHROME of a named app look — decision 0055. A ribbon, a tab
  // strip and a status bar are app-faithful components, so they are modules
  // with contracts rather than string branches in the renderer; the regions
  // they sit beside (explorer, editor, backlinks) are `REGION_PURPOSES`,
  // because a pane is a layout fact and a ribbon is a component. Atomic: a
  // status bar sawn across a page boundary is two half-bars.
  'workspace-ribbon':  { version: '0.1.0', fragmentation: 'atomic' },
  // The window's own controls, above the columns. Atomic for the reason the
  // others are: a traffic-light group sawn across a page boundary is two
  // halves of a window.
  'workspace-window':  { version: '0.1.0', fragmentation: 'atomic' },
  'workspace-tabs':    { version: '0.1.0', fragmentation: 'atomic' },
  'workspace-status':  { version: '0.1.0', fragmentation: 'atomic' },
  // The SEARCH tool — the query box and its count. A module, not a
  // renderer branch (@Codex, P1 on 93d57d7: 0055 wants app components
  // registered with contracts); the hits are an ordinary collection.
  'workspace-search':  { version: '0.1.0', fragmentation: 'atomic' },
  // Notion's shell — decision 0055's second named look. A NAV sidebar of
  // sections (upcoming, spaces, private), a TOPBAR of the page tab and
  // its share row, a PAGEHEAD of icon and title, and a VIEW SWITCHER of
  // the collection's views with its New button. Registered modules with
  // contracts, as the Obsidian ones are; atomic for the same reason.
  'workspace-nav':     { version: '0.1.0', fragmentation: 'atomic' },
  'workspace-topbar':  { version: '0.1.0', fragmentation: 'atomic' },
  'workspace-pagehead': { version: '0.1.0', fragmentation: 'atomic' },
  'view-switcher':     { version: '0.1.0', fragmentation: 'atomic' },
  /** Buttons as BLOCKS in a doc — Coda's first-class button row, each one a
   *  host action (create, and nothing the host cannot do). Decision 0055's
   *  third look. */
  'doc-actions':       { version: '0.1.0', fragmentation: 'atomic' },
  /** A RIBBON over a document — Word's tab row and band of grouped controls.
   *  Only the controls the host backs are drawn: style tiles that convert
   *  the caret's block, New, Print. Decision 0055's fourth look. */
  'doc-ribbon':        { version: '0.1.0', fragmentation: 'atomic' },
  'document-flow':     { version: '0.1.0', fragmentation: 'fragmentable' },
  'text-editing':      { version: '0.1.0', fragmentation: 'fragmentable' },
  'continuous-plot':   { version: '0.1.0', fragmentation: 'atomic' },
  'force-graph':       { version: '0.2.0', fragmentation: 'atomic' },
  'geospatial':        { version: '0.1.0', fragmentation: 'atomic' },
  'viewport-3d':       { version: '0.1.0', fragmentation: 'atomic' },
  'ink-capture':       { version: '0.1.0', fragmentation: 'atomic' },
  // A CALENDAR is grouping by a date the way a board is grouping by an enum.
  // Atomic: a month grid sawn in half across a page break is not a calendar,
  // and there is no smaller unit the layout can fall back to.
  'calendar':          { version: '0.1.0', fragmentation: 'atomic' },
  // A TIMELINE is a shared axis with one lane per object. Atomic for the same
  // reason the calendar is: half a shared axis is not a shorter timeline, it
  // is a chart with the scale cut off.
  'timeline':          { version: '0.1.0', fragmentation: 'atomic' },
} as const;
export type CapabilityId = keyof typeof CAPABILITIES;

/** Same set as a literal-typed array, so schemas keep their narrow types. */
export const CAPABILITY_IDS = [
  'workspace-ribbon', 'workspace-window', 'workspace-tabs', 'workspace-status', 'workspace-search',
  'workspace-nav', 'workspace-topbar', 'workspace-pagehead', 'view-switcher', 'doc-actions', 'doc-ribbon',
  'document-flow', 'text-editing', 'continuous-plot',
  'force-graph', 'geospatial', 'viewport-3d', 'ink-capture', 'calendar', 'timeline',
] as const satisfies readonly CapabilityId[];

/**
 * What a region IS, when it is something in particular — decision 0055.
 *
 * `relations` is the stack of one object's declared relations (the Connections
 * group). The rest are the PANES of a whole-workspace presentation: the file
 * tree, the note being read, the pane listing what points at it. They are
 * layout facts an app template requires and a taste cannot honestly ask for —
 * *"a taste cannot honestly require a file tree"* — which is why they are
 * words in the spec rather than classes a host paints. Declared, never
 * inferred from children: the `relations` docstring in `schema.ts` says why.
 */
export const REGION_PURPOSES = [
  'relations', 'explorer', 'editor', 'backlinks',
  // The PROPERTIES block of a note — Obsidian's, and every app's: a row of
  // named values under the title. The name is what the renderer adds; a
  // bare value row was the first deployed verdict's second finding
  // ("Claude    2026-08-14", no labels).
  'properties',
  // The right SIDEBAR and the GRAPH pane inside it — Obsidian's local graph
  // beside its backlinks, one visible at a time behind a strip of pane tabs.
  // A sidebar is a layout fact (which panes share one column); which pane
  // is showing is host state the renderer is told, never a spec word.
  'sidebar', 'graph',
  // The SEARCH pane — Obsidian's, in the left sidebar beside the explorer:
  // a query the host answers with the notes that match, as a subject
  // collection under the pane's list id (the backlinks pane's mechanism).
  'search',
] as const;
export type RegionPurpose = (typeof REGION_PURPOSES)[number];

/**
 * Named app looks the generator can be asked for BY NAME — decision 0055.
 *
 * A template is a whole-presentation constraint: it expands to required
 * region purposes, component families and visual tokens, and the generator
 * emits an ordinary versioned spec that carries the template's id as
 * provenance (`LayoutSpec.template`). Closed, so a genome can carry it as a
 * word and a person cannot smuggle a value through it.
 */
export const APP_TEMPLATES = ['app.obsidian', 'app.notion', 'app.coda', 'app.word', 'app.fibery', 'app.fabric'] as const;
export type AppTemplate = (typeof APP_TEMPLATES)[number];

/**
 * Each template's OWN version — the provenance a kept spec carries, and the
 * one the runtime DRAWS. One constant for both packages, so the generator
 * cannot stamp a version the renderer does not know: a kept reproduction
 * whose version this runtime cannot draw is refused in words rather than
 * silently repainted (0055 control 3; @Codex, P2 on ebf7b8c).
 */
export const APP_TEMPLATE_VERSIONS: Readonly<Record<AppTemplate, string>> = {
  // 0.2.0: the right column became a SIDEBAR with switchable backlinks and
  // graph panes. A kept 0.1.0 reproduction is a different topology, and the
  // runtime refuses it in words rather than reading two shapes as one
  // version (@Codex, P2 on 2c1ead8; 0055 control 3).
  // 0.3.0: the main column holds a GRAPH VIEW beside the editor — the whole
  // vault as a canvas behind a Graph view tab (the tenth verdict's list).
  // A kept 0.2.0 reproduction has no such view and is refused in words.
  // 0.4.0 — `workspace-window` became a MANDATORY first child of the workspace
  // row (0193/0194). @Codex's P2 on `bcbe1fc`, and decision 0059 is exactly
  // about this: a persisted 0.3.0 artifact begins with `workspace-ribbon`, so
  // leaving the stamp alone gives two incompatible contracts one version —
  // `templateShortfalls` would accept the stamp as current and then reject the
  // artifact against a topology it could not have had.
  'app.obsidian': '0.4.0',
  // 0.1.0: the page with its board — nav sidebar, topbar, page head, view
  // switcher, the collection as a board of status columns.
  'app.notion': '0.1.0',
  // 0.1.0: the doc with its table — page tree sidebar, topbar, a page head
  // with a cover, buttons as blocks, the collection as a table of pill and
  // avatar cells.
  'app.coda': '0.1.0',
  // 0.1.0: the document with the ribbon — title bar, ribbon, the sheet on
  // the desk (the document-flow module, paginated, with its contents), the
  // status bar with the word count.
  'app.word': '0.1.0',
  // 0.1.0: a database as a board — the white sidebar with the spaces as a
  // tree, the view bar with its star, the toolbar with the view's search
  // and New, the collection as state columns of badged cards, or as the
  // table with pill states and relation chips.
  // 0.2.0: the card carries a FOURTH information band — its parent relation
  // as a foot row under its own `fb-card-foot` slot, the reference's own
  // shape. A kept 0.1.0 reproduction has three bands and is a different
  // topology, so the runtime refuses it in words rather than repainting a
  // footer-less card as though the two were one version (@Codex, P1 on
  // 679dc4e; 0055 control 3, the same rule Obsidian's 0.2.0 and 0.3.0
  // follow). Caught because the band was added and this table was not
  // touched: saved reproductions would have silently kept three bands
  // while fresh ones got four.
  'app.fibery': '0.2.0',
  // 0.1.0: a note in a floating window with the assistant panel — the
  // title bar, the note (title, its media, its prose), the panel whose
  // composer is the library's own retrieval and whose answers are the
  // items it found.
  'app.fabric': '0.1.0',
};

/** Focus policy for a suggestion surface. Finding 02 from the prototype: a
 *  popover that takes focus on open reads as an interruption — the exact
 *  Clippy failure — but one that never holds focus leaves Escape nowhere to
 *  land. That is an affordance contract, not styling, so it lives here. */
export const FOCUS_POLICIES = ['never-steal', 'trap', 'follow'] as const;
export type FocusPolicy = (typeof FOCUS_POLICIES)[number];

/** Aspect ratios are a CLOSED SET, not a pattern. An unbounded `[0-9]+/[0-9]+`
 *  is a numeric covert channel: digits carry a phone number, a national ID, or
 *  base-10-encoded text just as well as letters do. A taste prior only ever
 *  needs a handful of ratios. */
/**
 * The three closed sets `schema.ts` declared as bare literals.
 *
 * Task `0127`. `pool.ts` opens by saying *"every scalar on a genome is NAMED
 * from a closed set (`fromClosedSet`), never copied out of the spec"*, and six
 * scalars were copied — three of them because the set they belong to existed
 * only as a `Type.Union` of literals in the schema, with no constant here for
 * `fromClosedSet` to take. A closed set with no name is a closed set nothing
 * can check against.
 */
export const FITS = ['cover', 'contain'] as const;
export type Fit = (typeof FITS)[number];

/** How a binding may be written. `read-write` is what makes a genome's
 *  `shape.writable` true. */
export const BINDING_DIRECTIONS = ['read', 'read-write'] as const;
export type BindingDirection = (typeof BINDING_DIRECTIONS)[number];

/** Which way a sort key runs. */
export const SORT_DIRECTIONS = ['asc', 'desc'] as const;
export type SortDirection = (typeof SORT_DIRECTIONS)[number];

export const ASPECTS = ['1/1', '4/3', '3/2', '16/9', '2/3', '9/16'] as const;
export type Aspect = (typeof ASPECTS)[number];

/** Slot names a capability may expose. Closed, because a free-form record KEY
 *  is exactly as wide a channel as a free-form value, and is easier to miss. */
export const CAPABILITY_SLOTS: Record<CapabilityId, readonly string[]> = {
  'workspace-ribbon': [],
  'workspace-window': [],
  'workspace-tabs':   [],
  'workspace-status': [],
  'workspace-search': [],
  'workspace-nav':    [],
  'workspace-topbar': [],
  'workspace-pagehead': [],
  'view-switcher':    [],
  'doc-actions':      [],
  'doc-ribbon':       [],
  'document-flow':   ['body', 'header', 'footer', 'footnotes'],
  'text-editing':    ['body'],
  'continuous-plot': ['marks', 'legend'],
  'force-graph':     ['node', 'legend'],
  'geospatial':      ['pin', 'legend'],
  'viewport-3d':     ['overlay'],
  'ink-capture':     [],
  'calendar':        ['legend'],
  'timeline':        ['legend'],
};

/** Named role channels a module accepts. Values are ordinary Bindings; only the
 * names are module-specific. Keeping the names closed here prevents a record key
 * becoming a content channel while letting the ordinary binding validator own
 * direction and mutation rules. */
export const CAPABILITY_CHANNELS: Record<CapabilityId, readonly string[]> = {
  'workspace-ribbon': [],
  // Nothing is read: a window control names no object and counts nothing.
  'workspace-window': [],
  // The tab strip names the open object; the status bar reads its word count.
  // Both READ — a tab is a label, not a place to retitle.
  'workspace-tabs':   ['label'],
  'workspace-status': ['words', 'label'],
  'workspace-search': [],
  // The page head and the topbar's tab name the page: a label channel, read.
  'workspace-nav':    [],
  'workspace-topbar': ['label'],
  'workspace-pagehead': ['label'],
  'view-switcher':    [],
  'doc-actions':      [],
  'doc-ribbon':       [],
  'document-flow':   ['body'],
  'text-editing':    ['body'],
  'continuous-plot': ['x', 'y', 'series'],
  'force-graph':     ['label', 'edge'],
  'geospatial':      ['position', 'label'],
  'viewport-3d':     ['model'],
  'ink-capture':     [],
  // `date` is what places an object on the grid; `label` is what it reads as
  // once it is there. Both required in practice, but requiredness is the
  // renderer's refusal to make, not the channel list's.
  'calendar':        ['date', 'label'],
  // `start` and `end` are the span; `date` is the fallback for an object that
  // has only one, which a timeline draws as a marker rather than a bar.
  'timeline':        ['start', 'end', 'date', 'label', 'lane'],
};

/** Role kinds admitted by each capability channel. Channel names alone are not
 * enough: a graph label may reach the DOM, while an edge must remain an opaque
 * reference resolved by the host. Empty entries deliberately impose no
 * narrower contract than the ordinary Binding validator yet. */
export const CAPABILITY_CHANNEL_ROLE_KINDS:
  Record<CapabilityId, Readonly<Record<string, readonly RoleKind[]>>> = {
    'workspace-ribbon': {},
    'workspace-window': {},
    'workspace-tabs':   { label: ['text'] },
    // `words` names the TEXT the bar counts, not a stored number: a status
    // bar reports the document under the cursor (task 0111's lesson), and
    // the host counts what the channel names (@Codex, P2 on ebf7b8c).
    'workspace-status': { words: ['text'], label: ['text'] },
    'workspace-search': {},
    'workspace-nav':    {},
    'workspace-topbar': { label: ['text'] },
    'workspace-pagehead': { label: ['text'] },
    'view-switcher':    {},
    'doc-actions':      {},
    'doc-ribbon':       {},
    'document-flow':   {},
    'text-editing':    {},
    'continuous-plot': {},
    'force-graph':     {
      label: ['text', 'number', 'date', 'enum', 'score'],
      edge: ['ref'],
    },
    'geospatial':      {},
    'viewport-3d':     {},
    'ink-capture':     {},
    'calendar':        {
      // A calendar places things in TIME. A text role in this channel would
      // be a grid keyed by a string, which is a board wearing a month's
      // clothes — so the channel refuses anything that is not a date.
      date: ['date'],
      label: ['text', 'number', 'date', 'enum', 'score'],
    },
    'timeline':        {
      start: ['date'],
      end: ['date'],
      date: ['date'],
      label: ['text', 'number', 'date', 'enum', 'score'],
      // A lane groups bars the way a board's columns group cards, so it is
      // the same closed answer: a low-cardinality value, not free text.
      lane: ['enum'],
    },
  };

/** Channels whose contract is narrower than a role kind. `source` is a ref,
 * but it is provenance rather than an object relationship and therefore
 * cannot be interpreted as a graph edge. Keeping the admitted roles here makes
 * validation, pooling and rendering share one boundary. */
export const CAPABILITY_CHANNEL_ROLES:
  Record<CapabilityId, Readonly<Record<string, readonly Role[]>>> = {
    'workspace-ribbon': {},
    'workspace-window': {},
    'workspace-tabs':   {},
    'workspace-status': {},
    'workspace-search': {},
    'workspace-nav':    {},
    'workspace-topbar': {},
    'workspace-pagehead': {},
    'view-switcher':    {},
    'doc-actions':      {},
    'doc-ribbon':       {},
    'document-flow':   {},
    'text-editing':    {},
    'continuous-plot': {},
    'force-graph':     {
      edge: RELATION_ROLES,
    },
    'geospatial':      {},
    'viewport-3d':     {},
    'ink-capture':     {},
    'calendar':        {},
    'timeline':        {},
  };

export function capabilityChannelAcceptsRole(
  capability: CapabilityId, channel: string, role: Role,
): boolean {
  const roles = CAPABILITY_CHANNEL_ROLES[capability]?.[channel];
  if (roles && !roles.includes(role)) return false;
  const kinds = CAPABILITY_CHANNEL_ROLE_KINDS[capability]?.[channel];
  return !kinds || kinds.includes(ROLE_KIND[role]);
}

/** Config is validated by SHAPE, not just by key name. The previous allowlist
 *  had the right instinct — fail closed on keys — and then failed open on
 *  values: `Type.Unknown()` let an allowlisted key hold an arbitrary object. An
 *  attacker only ever needed one legal key name. */
export type ConfigShape =
  | { type: 'boolean' }
  | { type: 'integer'; min: number; max: number }
  | { type: 'enum'; values: readonly string[] };

export const POOLABLE_CONFIG_SHAPES: Record<CapabilityId, Record<string, ConfigShape>> = {
  'workspace-ribbon': {},
  'workspace-window': {},
  'workspace-tabs':   {},
  'workspace-status': {},
  'workspace-search': {},
  'workspace-nav':    {},
  'workspace-topbar': {},
  'workspace-pagehead': {},
  'view-switcher':    {},
  'doc-actions':      {},
  'doc-ribbon':       {},
  'document-flow':   { columns: { type: 'integer', min: 1, max: 6 },
                       paginated: { type: 'boolean' },
                       footnotes: { type: 'boolean' },
                       // Whether the document opens with its contents. A
                       // layout decision and a poolable one — "people reading
                       // this shape want a way around it first" is exactly what
                       // a genome should be able to learn.
                       toc: { type: 'boolean' } },
  'text-editing':    { markup: { type: 'enum', values: ['inline', 'block', 'none'] },
                       showMarkers: { type: 'boolean' } },
  'continuous-plot': { mark: { type: 'enum', values: ['bar', 'line', 'point', 'area', 'arc'] },
                       scaleX: { type: 'enum', values: ['linear', 'time', 'ordinal'] },
                       scaleY: { type: 'enum', values: ['linear', 'log'] },
                       stacked: { type: 'boolean' } },
  // `pane`: drawn INSIDE A WORKSPACE PANE (0055, Obsidian's local graph) —
  // dots with small labels in the pane's own box rather than the page's
  // labelled buttons. Optional and additive, so 0.2.0 specs are untouched.
  // `vault`: the WHOLE vault as one canvas (0055, Obsidian's Graph view —
  // the tenth verdict's list): every note a dot sized by its degree, labels
  // on hover, in the main column's box. Optional and additive.
  'force-graph':     { depth: { type: 'integer', min: 1, max: 4 },
                       showLabels: { type: 'boolean' },
                       pane: { type: 'boolean' },
                       vault: { type: 'boolean' } },
  'geospatial':      { basemap: { type: 'enum', values: ['plain', 'terrain', 'satellite'] },
                       cluster: { type: 'boolean' } },
  'viewport-3d':     { lighting: { type: 'enum', values: ['studio', 'flat', 'dramatic'] },
                       autoRotate: { type: 'boolean' } },
  'ink-capture':     { pressure: { type: 'boolean' }, smoothing: { type: 'boolean' } },
  // `weekStart` is a locale fact, not a preference, and it is poolable for the
  // same reason the rest of this table is: which day a week begins on is
  // something a pool of people agree about. Stored as an integer 0-6 rather
  // than an enum of day names so it needs no translation to be used.
  'calendar':        { granularity: { type: 'enum', values: ['month', 'week'] },
                       weekStart: { type: 'integer', min: 0, max: 6 },
                       showEmpty: { type: 'boolean' } },
  'timeline':        { granularity: { type: 'enum', values: ['day', 'month', 'year'] },
                       showToday: { type: 'boolean' } },
};

/**
 * What an action is allowed to do — decision 0014.
 *
 * The boundary that does not move is:
 *
 *   **The spec may NAME an action. It may never CONTAIN one.**
 *
 * That is the same sentence argued for formulas in 0004 — the spec may name an
 * aggregation from a closed set, never an expression — and it generalizes
 * without modification. An automation is behaviour, and behaviour in the layout
 * spec means the presentation layer executes author-supplied code, which every
 * privacy and safety property in this repo rests on it not doing.
 *
 * There is deliberately NO closed list of product actions: @Codex's amendment,
 * and it is right, because a permanent enumeration would create a second
 * authority alongside the open module registry established in 0010 — an
 * argument I made myself there, so being talked out of it here is consistency.
 *
 * What is closed is **this set** and the descriptor shape. An action declares
 * the effects it may have, the runtime refuses anything it did not declare, and
 * that is exactly the contract `MUTATIONS` already gives a write binding.
 */
export const EFFECTS = [
  /** Writes a property on an object that already exists. */
  'set-property',
  /** Brings a new object into being. */
  'create-object',
  /** Moves the view. Touches no data, which is why it is separate — an action
   *  that only navigates should not have to claim a write. */
  'navigate',
  /** Restructures the layout itself. Separate from every data effect because it
   *  changes what a person SEES rather than what is stored, and because a
   *  viewer holding two field grants must not be able to rearrange a lens —
   *  decision 0015 (F21). The drop engine refuses without it. */
  'edit-layout',
] as const;
export type Effect = (typeof EFFECTS)[number];

/**
 * An action name is `namespace.name`, and the namespace is not decoration.
 *
 * The registry is open, so this string is author-controlled and reaches the
 * same places a capability id does. Two defences: the shape is narrow enough
 * that it cannot carry a sentence, and the pool gate does not carry it at all —
 * it carries the DECLARED EFFECTS instead, which are a closed set. "This card
 * has a button that writes a property" is the poolable taste; which action
 * implements it is tenant-specific and nobody else's business.
 */
export const ACTION_NAME = /^[a-z][a-z0-9-]{0,23}\.[a-z][a-z0-9-]{0,23}$/;

/**
 * Bumped together, and both for real reasons.
 *
 * `SPEC_VERSION` is the SHAPE of a spec: `placement` and `invocation` are new
 * node fields, so a 0.1.0 reader cannot parse a 0.2.0 spec.
 *
 * `VOCABULARY_VERSION` is the WORDS: `media.video`, `media.audio`,
 * `media.still` and `transcript` are new roles, `av` is a new role kind, and
 * `invoke` and the effect set are new. A 0.1.0 validator REFUSES every one of
 * them — so leaving the number alone would mean two validators disagreeing
 * about what "0.1.0" permits, and a pooled genome recording a version whose
 * meaning had quietly changed underneath it. Found by @Codex; the placement and
 * invocation halves of the debt were mine and had gone unbumped for two merges.
 *
 * **`duplicate-object` was added here and taken back out the same day.** Both
 * numbers moved for it and both moved back: decision 0032 was ruled on a false
 * premise and superseded by 0033 before the word ever shipped. Worth one line
 * because the bump taught something true — I expected only the WORDS to move,
 * and the guard said both, since `schema.ts` builds `invocation.effects` as
 * `enumOf(V.EFFECTS)`. An enum value is a word until a schema embeds the enum,
 * and this one does.
 *
 * There is still no N/N-1 machinery — decision 0002 records that as known debt
 * — so this is a hard break rather than a migration. It is cheap now and gets
 * more expensive with every spec anyone stores.
 *
 * **SPEC 0.11.0 / VOCABULARY 0.12.0, 2026-08-23: two gestures.** `attach` and
 * `detach` join `AFFORDANCES`. A grant's `via` is what stops one gesture
 * performing another write — an `edit-inline` grant does not authorise a drag
 * between groups — so the two relation mutations need words of their own, and
 * reusing `select` or `invoke` would let any button on the page detach a
 * relation.
 *
 * **Both numbers move, and the first draft of this entry said only the
 * vocabulary did.** `AFFORDANCES` is compiled INTO the schema as an enum, so a
 * new word widens the shape a validator accepts and a 0.10.0 reader refuses a
 * spec that uses one. The guard caught it: the SHAPE fingerprint moved with the
 * WORDS fingerprint, which is the case the two-number scheme exists to tell
 * apart and this time they genuinely coincide.
 *
 * **SPEC 0.10.0 / VOCABULARY 0.11.0, 2026-08-23: both, and for once for the
 * same change.** Decision 0016's authoring half. `link` and `unlink` are new
 * WORDS, and `RELATION_ROLES` and `MUTATION_BINDING_SITES` are new tables that
 * change what a validator permits without changing a schema byte — so the
 * vocabulary moves. `node-slot.editable` is retired for
 * `mutations?: ('link' | 'unlink')[]` — a node lost a field and gained another
 * — so the spec version moves too.
 *
 * The boolean is NOT aliased. 0016 is explicit: *"`editable: true` must fail
 * validation with a message naming `mutations`. It must never normalize to
 * `['link', 'unlink']`."* Attach and detach are distinct authorities, and a
 * boolean asked to stand for both can only grant the larger one — which means
 * silently granting deletion to every spec that ever said `editable: true`.
 *
 * **SPEC 0.9.0, 2026-08-23: a node gained a field, and a leaf stopped being
 * one.** `node-slot` may carry `children` — the spec a referenced object is
 * drawn with. A 0.8.0 reader parses a 0.9.0 spec and silently draws its own
 * preview instead of the one the spec asked for, which is exactly the "two
 * readers disagreeing about one version" this number exists to prevent.
 *
 * `VOCABULARY_VERSION` does NOT move: no word changed. `node-slot`,
 * `children`, and every kind admitted in it were already words. What changed is
 * the SHAPE — and `node-slot` left `LEAF_KINDS`, which is a grammar fact rather
 * than a vocabulary one.
 *
 * **0.10.0, 2026-08-23: words only — a table that moved packages.**
 * `MUTATION_ROLE_KINDS` was in `@morph/store` and is now here, which decision
 * 0016 authorised. `SPEC_VERSION` does not move: no node gained a field and
 * the schema is byte-identical. What moved is what a validator PERMITS — 118
 * `(mutation, role)` pairs that used to validate now do not — and two
 * validators disagreeing about what one version permits is precisely what
 * these numbers exist to prevent. The guard found this by doing its job: the
 * fingerprint changed the moment the table entered it.
 *
 * **0.6.0, 2026-08-22: words only, again.** `document-flow` gained a `toc`
 * config key. A validator that has not heard of it refuses the spec with
 * `unknown-config-key` — which is how this bump was found, by the validator
 * doing its job rather than by anyone remembering.
 *
 * **0.5.0, 2026-08-21: words only.** `media.document` is a new role and `doc`
 * is a new role kind, so `VOCABULARY_VERSION` moves and `SPEC_VERSION` does
 * not — the SHAPE of a spec is unchanged, no node gained a field, and a 0.4.0
 * reader parses a 0.5.0 spec fine right up until it meets a word it has never
 * heard. That is precisely the distinction these two numbers exist to draw,
 * and the first time a bump has exercised only one of them.
 */
/**
 * Where a collection's objects come from — as a ROLE, never as an address.
 *
 * `renderHTML` took one `objects` array for the whole spec, so every collection
 * in a lens showed the same set. That is why the demo's outline panel lists a
 * single row the moment you open an object: the panel is a collection inside
 * the same spec as the content, and it faithfully renders what the content
 * renders. A sidebar that answers "what is here" must not shrink to one when
 * you open something.
 *
 * The obvious fix — let a collection name a pool or carry a scope — is the one
 * that must not be taken. A scope holds a manifest ref, and a spec is POOLED:
 * anything a spec carries is a thing the taste model can learn and another
 * tenant can be shown. So a collection names a role and the host resolves it,
 * exactly as a binding names a role and the manifest resolves it.
 *
 * Closed, and deliberately small:
 *   `subject`  — what this lens is about. The default, and what every existing
 *                collection means.
 *   `siblings` — everything alongside the subject. A file list, a pool, the
 *                set you would return to by pressing Escape.
 *
 * A third entry needs an argument, not a use case: each one is a thing every
 * host must now be able to answer.
 */
export const SOURCES = ['subject', 'siblings'] as const;
export type Source = (typeof SOURCES)[number];

// Named capability channels change both the accepted spec grammar and the
// words preserved by pooled genomes, so this is a real hard version boundary.
// 0.14.0 replaces the schema's closed capability-name enum with a bounded
// module-id syntax. Whether a name RESOLVES is now a semantic fact settled by
// `validateSpec` against the registry, so a module can become valid at runtime
// without its manifest ever entering a spec.
// SPEC 0.16.0 / VOCABULARY 0.14.0, 2026-09-02: decision 0055. Both move. A
// spec gained a field (`template`, the named app look it was generated under)
// and `region.purpose` went from one literal to the `REGION_PURPOSES` enum —
// SHAPE. Three workspace modules, four purposes and the template names are
// new WORDS a 0.13.0 validator refuses.
// SPEC 0.17.0 / VOCABULARY 0.15.0, same day: `properties` joins
// `REGION_PURPOSES` — a WORD, and the enum is embedded in the schema, so the
// SHAPE moves with it.
// VOCABULARY 0.16.0: the status bar's `words` channel counts a TEXT role.
// SPEC 0.18.0 / VOCABULARY 0.17.0: `sidebar` and `graph` join
// `REGION_PURPOSES` — Obsidian's local graph as a switchable right-pane
// state (task 0170, @Codex's third deployed verdict). Words, and the
// embedded enum moves the shape.
// SPEC 0.19.0 / VOCABULARY 0.18.0: `search` joins `REGION_PURPOSES` — the
// ribbon's Search tool with a real surface behind it (task 0170, @Codex's
// eighth verdict).
export const SPEC_VERSION = '0.25.0';
export const VOCABULARY_VERSION = '0.27.0';

/**
 * Does this string reference something outside the document?
 *
 * A layout spec has no legitimate reason to contain a URL. Every media role
 * resolves to one at **render** time, through the private binding manifest —
 * the URL is data, and data never enters a spec or the pool. So a URL-shaped
 * string in a spec is either a mistake or a beacon, and both fail closed.
 *
 * @Codex named this control in task 0001 and it was never implemented: grepping
 * the package for `format: 'uri'` returned zero.
 *
 * Rejects ANY RFC-style scheme prefix, not just `scheme://`. The first version
 * required the double slash and @Codex broke it on review in five ways —
 * `mailto:`, `urn:`, `ipfs:`, `https:evil.example/path`, and any custom
 * `beacon:<identifier>` — all of which are external references and all of which
 * walked straight through. A denylist of known-bad schemes is the wrong shape
 * for the same reason a denylist is always the wrong shape here.
 *
 * **Normalized before matching, because the parser normalizes too.** An
 * adversarial pass then found nine more bypasses, each checked against Node's
 * WHATWG `URL` — the algorithm a browser actually runs. It strips ASCII TAB, LF
 * and CR from *anywhere* in the input, and strips leading C0 controls. So
 * `ht<TAB>tp://evil.example/x` is `http://evil.example/x` to every real
 * consumer and an ordinary string to a naive regex. Matching the raw value
 * while every consumer matches a normalized one is the whole bug class, and
 * `trim()` alone does not close it: it removes neither an interior TAB nor a
 * leading NUL.
 *
 * **Authority detection covers backslashes.** `/\evil.example/x`,
 * `///evil.example/x` and `\\evil.example\x` all resolve off-origin.
 *
 * **An opaque identifier is not a resource reference.** A `urn:` or `did:` with
 * no authority names something; it does not locate it, and there is nothing to
 * fetch. `bindingManifestRef` is documented as an opaque reference to the
 * private slot map, and a URN is the canonical shape for exactly that — the
 * previous rule refused six of ten realistic values for that field. One that
 * *does* carry an authority (`urn://host`) is not the thing being allowed.
 *
 * Bare dotted hostnames are still not matched, and that is deliberate:
 * `media.poster-frame` and `relation.peer` are roles, and a rule that fires on
 * those refuses every real spec, which is a worse outcome than the leak. No
 * value in the closed vocabulary contains a colon, so the scheme rule costs
 * nothing legitimate — checked across every closed set, not assumed.
 */
export function looksExternal(value: string): boolean {
  // The same normalization the URL parser performs, so the two cannot disagree.
  const v = value
    .replace(/[\t\n\r]/g, '')      // stripped from anywhere by the URL parser
    .replace(/^[\x00-\x20]+/, '')  // leading C0 controls and space, likewise
    .replace(/[\x00-\x20]+$/, '');

  // Identifier, not locator: nothing to fetch, so nothing to refuse.
  if (/^(?:urn|did):(?![/\\])/i.test(v)) return false;

  return /^[a-z][a-z0-9+.-]*:/i.test(v)   // ANY scheme: mailto:, https:x, beacon:
      || /^[/\\]{2,}/.test(v)             // //host, ///host, \\host, /\host
      || /^www\./i.test(v);
}

/** The partition's shape as `validate.ts` reads it. Declared here rather than
 *  imported from `schema.ts` because that module imports this one, and the
 *  semantic pass needs the shape without the cycle. */
export interface TPartitionShape {
  readonly valueFrom: number;
  readonly branches: readonly string[];
  readonly unset: { readonly show: string };
}
